import { useState, useEffect, useCallback } from 'react';
import { categoriesAPI, authAPI, householdsAPI } from '../api/client';

export interface Category {
  id: string;
  name: string;
  color: string;
  isFixed: boolean;
  targetPercentage?: number;
  limitAmount?: number;
}

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [householdId, setHouseholdId] = useState<string | null>(null);

  // Fetch household and categories
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        // Get user and household
        const authResponse = await authAPI.getUser();
        if (authResponse.success && authResponse.data) {
          const { households } = authResponse.data;
          let currentHouseholdId: string;

          if (households && households.length > 0) {
            currentHouseholdId = households[0].id;
          } else {
            // Try to create a default household if none exists
            try {
               const createResponse = await householdsAPI.createHousehold({ name: 'Personal Budget' });
               if (createResponse.success && createResponse.data) {
                 currentHouseholdId = createResponse.data.household.id;
               } else {
                 console.error('Failed to create default household');
                 setIsLoading(false);
                 return;
               }
            } catch (e) {
               console.error('Error creating default household:', e);
               setIsLoading(false);
               return;
            }
          }

          setHouseholdId(currentHouseholdId);

          // Fetch categories
          const categoriesResponse = await categoriesAPI.getCategories(currentHouseholdId);
          if (categoriesResponse.success && categoriesResponse.data) {
            setCategories(categoriesResponse.data.categories.map((cat: any) => ({
              id: cat.id,
              name: cat.name,
              color: cat.color,
              isFixed: cat.is_fixed || cat.isFixed,
              targetPercentage: cat.target_percentage || cat.targetPercentage,
              limitAmount: cat.limit_amount || cat.limitAmount
            })));
          }
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const refreshCategories = useCallback(async () => {
    if (!householdId) return;
    try {
      const response = await categoriesAPI.getCategories(householdId);
      if (response.success && response.data) {
        setCategories(response.data.categories.map((cat: any) => ({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          isFixed: cat.is_fixed || cat.isFixed,
          targetPercentage: cat.target_percentage || cat.targetPercentage,
          limitAmount: cat.limit_amount || cat.limitAmount
        })));
      }
    } catch (error) {
      console.error('Error refreshing categories:', error);
    }
  }, [householdId]);

  // Add a new category
  const addCategory = async (name: string, color: string = '#6366F1', targetPercentage?: number, limitAmount?: number, isFixed: boolean = false) => {
    if (!householdId) throw new Error('No household found');

    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error('Category name cannot be empty');
    }

    try {
      const response = await categoriesAPI.createCategory(householdId, {
        name: trimmedName,
        color,
        is_fixed: isFixed,
        targetPercentage,
        limitAmount
      });

      if (response.success) {
        await refreshCategories();
        return response.data?.category;
      } else {
        throw new Error(response.error || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  };

  // Update category details
  const updateCategory = async (id: string, updates: Partial<Omit<Category, 'id' | 'isFixed'>>) => {
    if (!householdId) throw new Error('No household found');

    try {
      const response = await categoriesAPI.updateCategory(householdId, id, {
        name: updates.name,
        color: updates.color,
        targetPercentage: updates.targetPercentage,
        limitAmount: updates.limitAmount
      });
      
      if (response.success) {
        await refreshCategories();
      } else {
        throw new Error(response.error || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  };

  // Delete a category
  const deleteCategory = async (id: string) => {
    if (!householdId) throw new Error('No household found');

    try {
      const response = await categoriesAPI.deleteCategory(householdId, id);
      if (response.success) {
        await refreshCategories();
      } else {
        throw new Error(response.error || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  // Get category by ID
  const getCategoryById = (id: string): Category | undefined => {
    return categories.find(category => category.id === id);
  };

  // Reset to mock data - No longer applicable for API-based approach
  const resetToDefault = async () => {
    console.warn('resetToDefault is not supported in API mode');
  };

  return {
    categories,
    isLoading,
    addCategory,
    updateCategory,
    deleteCategory,
    getCategoryById,
    resetToDefault
  };
}